/**
 * The one button every toolbar row is built from, plus the group divider.
 *
 * `size` exists so the bare wiki-comment cluster can render at its host
 * row's 28px rhythm (`sm`) while every other row stays at 32px (`md`) —
 * one vertical rhythm per row, never two. `tone="subtle"` is the
 * Slack-style idle/pressed treatment the chat composer uses.
 *
 * Focus ring is `focus-visible` (matches the global `<Button>`): a mouse
 * click on a formatting button must not flash a ring.
 */

import type { MouseEvent, ReactNode } from 'react'
import { forwardRef } from 'react'

export interface ToolbarButtonProps {
  label: string
  onClick: (event: MouseEvent<HTMLButtonElement>) => void
  active?: boolean
  children: ReactNode
  /**
   * `'subtle'` softens idle to `text-fg-muted` and pressed to a neutral
   * `bg-bg-muted` (Slack-style), instead of the accent fill the default
   * tone uses. The chat toolbar passes `tone="subtle"` so its idle row
   * reads light against the composer's `bg-bg-elevated` surface.
   */
  tone?: 'default' | 'subtle'
  /** `'md'` = 32px (default). `'sm'` = 28px, the bare comment-cluster size. */
  size?: 'md' | 'sm'
  disabled?: boolean
  /** Extra ARIA for triggers that open a popover / menu. */
  ariaHasPopup?: 'menu' | 'dialog' | 'listbox'
  ariaExpanded?: boolean
  ariaControls?: string
  /** Set when the button is a pure trigger (no pressed semantics). */
  omitPressed?: boolean
}

export const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  function ToolbarButton(
    {
      label,
      onClick,
      active = false,
      children,
      tone = 'default',
      size = 'md',
      disabled = false,
      ariaHasPopup,
      ariaExpanded,
      ariaControls,
      omitPressed = false,
    },
    ref,
  ) {
    // Active state for the subtle (chat) tone reads as a clear accent
    // tint so "this mark is currently applied" stands out from the gray
    // hover state. Default tone keeps its accent fill.
    const activeClasses = active
      ? tone === 'subtle'
        ? 'bg-accent/15 text-accent ring-1 ring-accent/25 hover:bg-accent/20'
        : 'bg-accent text-accent-fg hover:opacity-90'
      : tone === 'subtle'
        ? 'text-fg-muted hover:bg-bg-muted hover:text-fg'
        : 'text-fg hover:bg-bg-muted'
    const sizeClasses =
      size === 'sm' ? 'h-7 min-w-[28px] px-1.5 text-sm' : 'h-8 min-w-[32px] px-2 text-base'
    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        title={label}
        aria-pressed={omitPressed ? undefined : active}
        aria-haspopup={ariaHasPopup}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        className={`inline-flex shrink-0 items-center justify-center rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-60 ${sizeClasses} ${activeClasses}`}
      >
        {children}
      </button>
    )
  },
)

/** Group boundary. Semantic token so it adapts across the three themes. */
export function Divider() {
  return <span aria-hidden="true" className="mx-0.5 h-5 w-px shrink-0 bg-border" />
}
